import type { PostHog } from 'posthog-js'
import type { ImpersonationTarget } from '~/client/common/stores/auth'
import { BUILD_VERSION } from './build-info'

// posthog-js is loaded lazily via dynamic import to prevent its <script> tag
// injection from interfering with React hydration.
let _posthog: PostHog | null = null
let _initPromise: Promise<void> | null = null

export enum PostHogEvent {
  PRESENTATION_GENERATED = 'presentation_generated',
  CITY_EXPANSION_REQUESTED = 'city_expansion_requested',
  PROPOSAL_ASSISTANT_ERROR = 'proposal_assistant_error',
  PROPOSAL_ASSISTANT_OPENED = 'proposal_assistant_opened',
  PROPOSAL_ASSISTANT_CLOSED = 'proposal_assistant_closed',
  PROPOSAL_ASSISTANT_STEP_VIEWED = 'proposal_assistant_step_viewed',
  PROPOSAL_ASSISTANT_PROCESSING_STARTED = 'proposal_assistant_processing_started',
  PROPOSAL_ASSISTANT_UPDATES_APPLIED = 'proposal_assistant_updates_applied',
  PROPOSAL_ASSISTANT_ATTACHMENTS_SAVED = 'proposal_assistant_attachments_saved',
  PROPOSAL_ASSISTANT_ATTACHMENT_PREVIEW_OPENED = 'proposal_assistant_attachment_preview_opened',
  PROPOSAL_ASSISTANT_NO_UPDATES_FOUND = 'proposal_assistant_no_updates_found',
  PROPOSAL_ASSISTANT_PARSE_FAILED = 'proposal_assistant_parse_failed',
  SLIDE_STUDIO_OPENED = 'slide_studio_opened',
  SLIDE_STUDIO_GENERATED = 'slide_studio_generated',
  SLIDE_STUDIO_GALLERY_INSERT = 'slide_studio_gallery_insert',
  REVEL_RECS_BRIEF_SUBMITTED = 'revel_recs_brief_submitted',
  ADMIN_RECS_STATUS_CHANGED = 'admin_recs_status_changed',
  ADMIN_RECS_ASSIGNEE_CHANGED = 'admin_recs_assignee_changed',
  ADMIN_RECS_REORDERED = 'admin_recs_reordered',
  SRP_SPACE_CARD_VIEW_MORE_SPACES = 'srp_space_card_view_more_spaces',
  AI_SUMMARY_SOURCE_CLICKED = 'ai_summary_source_clicked',
  PRESENT_CREDENZA_EXPANDED_STATE_SHOWN = 'expanded state shown',
  PRESENT_CREDENZA_NEW_CLICKED = 'New clicked',
  PRESENT_CREDENZA_CONTACT_SALES_CLICKED = 'Contact Sales clicked',
  MAP_REDO_SEARCH_CLICKED = 'map_redo_search_clicked',
  SRP_FACET_APPLIED = 'srp_facet_applied',
  SRP_FACETS_RESET = 'srp_facets_reset',
  SRP_VIBE_SEARCH_SUBMITTED = 'srp_vibe_search_submitted',
  SRP_LOCATION_SELECTED = 'srp_location_selected',
  SRP_VENUE_NAME_SELECTED = 'srp_venue_name_selected',
  SPACE_CARD_CLICKED = 'space_card_clicked',
  SPACE_CARD_SAVED = 'space_card_saved',
  SPACE_DETAIL_CONTACT_CLICKED = 'space_detail_contact_clicked',
  SPACE_DETAIL_GALLERY_OPENED = 'space_detail_gallery_opened',
  LOGIN_SUBMITTED = 'login_submitted',
  PASSWORD_RESET_REQUESTED = 'password_reset_requested',
  ACTIVATION_REQUESTED = 'activation_requested',
  VRM_PRESENTATION_EDIT_OPENED = 'vrm_presentation_edit_opened',
  VRM_PRESENTATION_EXPORTED = 'vrm_presentation_exported',
  VRM_EMAIL_SUMMARY_GENERATED = 'vrm_email_summary_generated',
  VRM_COLUMNS_CHANGED = 'vrm_columns_changed',
  VRM_STATUS_CHANGED = 'vrm_status_changed',
  VRM_SEND_INQUIRY_OPENED = 'vrm_send_inquiry_opened',
  VRM_INQUIRY_SENT = 'vrm_inquiry_sent',
  VRM_INQUIRY_SEND_FAILED = 'vrm_inquiry_send_failed',
  VRM_TRAVEL_TIMES_OPENED = 'vrm_travel_times_opened',
  VRM_TRAVEL_TIMES_CALCULATED = 'vrm_travel_times_calculated',
  VRM_TRAVEL_TIMES_FAILED = 'vrm_travel_times_failed',
  VRM_ATTACHMENT_SHARE_TOGGLED = 'vrm_attachment_share_toggled',
  VRM_ASSET_UPLOADED = 'vrm_asset_uploaded',
  VRM_ASSET_INCLUDE_TOGGLED = 'vrm_asset_include_toggled',
  VRM_EVENT_FIELD_EDITED = 'vrm_event_field_edited',
}

export function initPostHog() {
  if (_initPromise) return _initPromise

  const posthogKey = import.meta.env.VITE_POSTHOG_KEY
  const posthogHost = import.meta.env.VITE_POSTHOG_HOST

  if (typeof window === 'undefined' || !posthogKey || !posthogHost) {
    return Promise.resolve()
  }

  _initPromise = import('posthog-js').then((mod) => {
    _posthog = mod.default
    _posthog.init(posthogKey, {
      api_host: posthogHost,
      defaults: '2025-11-30',
      person_profiles: 'identified_only',
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      loaded: (ph: PostHog) => {
        ph.register({
          buildVersion: BUILD_VERSION,
        })
        if (import.meta.env.MODE === 'development') {
          // Expose to console so devs can call posthog.debug(), posthog.register(...), etc.
          // Stripped from preview/production builds because the gate is build-time.
          ;(window as unknown as { posthog?: PostHog }).posthog = ph
          console.log('PostHog initialized:', {
            api_host: posthogHost,
            distinct_id: ph.get_distinct_id(),
          })
        }
      },
    })
  })

  return _initPromise
}

/**
 * Lazy PostHog proxy — safe to import at the top level.
 * All method calls silently no-op until initPostHog() completes.
 */
const noop = () => {}
export const posthog: PostHog = new Proxy({} as PostHog, {
  get(_, prop) {
    if (_posthog) {
      const value: unknown = _posthog[prop as keyof PostHog]
      if (typeof value === 'function') {
        return (...args: unknown[]): unknown => Reflect.apply(value, _posthog, args)
      }
      return value
    }
    return noop
  },
})

/**
 * Fire a PostHog event. Best-effort: swallows errors so analytics can never
 * block a user action. No-ops until initPostHog() completes (via the proxy).
 */
export function capture(event: PostHogEvent, properties?: Record<string, unknown>) {
  try {
    posthog?.capture?.(event, properties)
  } catch {
    // analytics is best-effort — never block the user action
  }
}

// ─── Fetch interceptor — PostHog headers ─────────────────────────────────────
// Patches window.fetch to attach PostHog session & identity headers on
// same-origin requests so the backend can link logs to session replay.

let _fetchPatched = false

export function patchFetchWithPostHogHeaders() {
  if (typeof window === 'undefined' || _fetchPatched) return
  _fetchPatched = true

  const originalFetch = window.fetch.bind(window)

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // Only patch same-origin requests
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    const isSameOrigin = url.startsWith('/') || url.startsWith(window.location.origin)

    if (isSameOrigin && _posthog) {
      const sessionId = _posthog.get_session_id?.()
      const distinctId = _posthog.get_distinct_id?.()

      if (sessionId || distinctId) {
        const headers = new Headers(init?.headers)
        if (sessionId) headers.set('x-posthog-session-id', sessionId)
        if (distinctId) headers.set('x-posthog-distinct-id', distinctId)
        return originalFetch(input, { ...init, headers })
      }
    }

    return originalFetch(input, init)
  }
}

// ─── Fetch interceptor — Impersonation header ─────────────────────────────────
// Patches window.fetch to inject X-Impersonate-Token on same-origin /api/**
// requests when an active impersonation token is in the auth store.
// Excluded: /api/admin/impersonation/start (no token exists at call time).
// Also handles 401 responses with impersonation_* error codes.

let _impersonationFetchPatched = false

export function patchFetchWithImpersonationHeader() {
  if (typeof window === 'undefined' || _impersonationFetchPatched) return
  _impersonationFetchPatched = true

  const originalFetch = window.fetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url

    const isApi =
      url.startsWith('/api/') ||
      (window.location.origin.length > 0 && url.startsWith(`${window.location.origin}/api/`))

    const isStartEndpoint =
      url.includes('/api/admin/impersonation/start')

    // Only intercept same-origin /api/** requests, excluding the /start endpoint.
    if (!isApi || isStartEndpoint) {
      return originalFetch(input, init)
    }

    // Lazy import to avoid circular dependency at module load time.
    const { useAuthStore } = await import('~/client/common/stores/auth')
    const token = useAuthStore.getState().impersonationToken

    if (!token) {
      return originalFetch(input, init)
    }

    const headers = new Headers(init?.headers)
    headers.set('X-Impersonate-Token', token)
    const res = await originalFetch(input, { ...init, headers })

    // Handle 401 responses with impersonation error codes by clearing local state.
    if (res.status === 401) {
      try {
        const body: unknown = await res.clone().json()
        const code =
          body !== null &&
          typeof body === 'object' &&
          'code' in body &&
          typeof (body as Record<string, unknown>).code === 'string'
            ? (body as Record<string, unknown>).code as string
            : null

        if (code !== null && code.startsWith('impersonation_')) {
          useAuthStore.getState().clearImpersonationLocal()

          // Dynamic import of sonner to avoid adding a dep at the top level.
          const { toast } = await import('sonner')
          toast.error('Impersonation session ended')
        }
      } catch {
        // Body parsing failed — ignore, return original response.
      }
    }

    return res
  }
}

// ─── PostHog super-properties for impersonation ───────────────────────────────

/**
 * Register or unregister impersonation super-properties on PostHog.
 * Called from auth store startImpersonation / stopImpersonation / clearImpersonationLocal.
 * INVARIANT: never calls posthog.identify with the target user id.
 */
export function setImpersonationSuperProperties(viewAsUser: ImpersonationTarget | null) {
  if (!_posthog) return
  if (viewAsUser) {
    _posthog.register({ is_impersonating: true, impersonating_user_id: viewAsUser.id })
  } else {
    _posthog.unregister('is_impersonating')
    _posthog.unregister('impersonating_user_id')
  }
}

// ─── Centralized identity sync ────────────────────────────────────────────────

/**
 * syncIdentity — single source of truth for PostHog identify.
 *
 * Reads the auth store and calls posthog.identify using the ADMIN's identity only.
 * INVARIANT: NEVER called with the target user's id. viewAsUser.id never reaches here.
 * Called by the auth store after login / refreshUserDetail; also available for
 * explicit call sites that need to re-identify.
 */
export async function syncIdentity(): Promise<void> {
  // Lazy import to avoid circular dependency at module load time.
  const { useAuthStore } = await import('~/client/common/stores/auth')
  const { user, userDetail } = useAuthStore.getState()
  if (!user || !userDetail || !_posthog) return
  // INVARIANT: always admin identity — viewAsUser never passed here.
  _posthog.identify(user.id, {
    email: userDetail.email,
    name: [userDetail.first_name, userDetail.last_name].filter(Boolean).join(' ') || undefined,
    first_name: userDetail.first_name,
    last_name: userDetail.last_name,
    entity_name: userDetail.active_entity?.name,
    entity_type: userDetail.active_entity?.type,
    is_staff: userDetail.is_staff,
    is_superuser: userDetail.is_superuser,
  })
}
